// apiService.js
import { withDevice } from '@/utils/sender-device'
import { store, static_store } from './store.js'; // Import the store
import axios from 'axios';
axios.defaults.withCredentials = true;
import { setKeysFromSource, parseAxiosError, getCsrfToken, clientToday } from './utils/general-utils.js'
import { track, setUser } from './utils/analytics.js'
import { profileFieldRejection } from './utils/profile-safety.js'
import { clasp, openClasp } from './composables/useJournalClasp.js'

// ─── Querent / Auth ─────────────────────────────────────────────── //

async function getQuerent(querent_id) {
  try {
    // The session proves who is asking: the server answers only the signed-in
    // owner (email, birth data, readings).
    const response = await axios.get(static_store.apiUrl + '/reading/get-querent/',
      { params: { querent_id, session_id: store.session_id } });
    if (response?.data){
      setKeysFromSource(response.data, store)
      // The balance chip stays blank until a server has actually told us a
      // figure — see store.querent_carat_balance_loaded.
      if (typeof response.data.querent_carat_balance === 'number') {
        store.querent_carat_balance_loaded = true
      }
    }
    if (response?.data?.reading_sessions){
      store.reading_sessions = response.data.reading_sessions.filter((x) => x.initial_question)
    }
    return response
  } catch (error) {
    console.error('Error fetching querent:', error);
  }
}

async function resetPassword(token, password) {
  try {
    const response = await axios.post(static_store.apiUrl + '/auth/reset-password/', { token, password });
    console.log('resetting password', response)
    return response
  } catch (error) {
    console.error('Error resetting password:', error);
    return { status: 'fail', message: parseAxiosError(error) }
  }
}

// ─── Reading Session ────────────────────────────────────────────── //

// ─── Sharing ────────────────────────────────────────────────────── //

async function toggleShare(reading_session_id) {
  try {
    // Owner only: the server checks this session owns the reading.
    const response = await axios.post(static_store.apiUrl + '/reader/toggle-share/', {
      reading_session_id: reading_session_id, session_id: store.session_id
    });
    // Return on success AND on the resonance gate (ok:false, needs_resonance) so
    // the caller can prompt the seeker to rate; only a malformed reply is dropped.
    if (response?.data?.reading_session || response?.data?.reason) {
      return response
    }
  } catch (error) {
    console.error('Error fetching sharing toggle:', error);
  }
}

// Record the seeker's 1-5 crystal-ball rating of their own reading (upsert).
// Returns { rating, average_rating, rating_count }.
async function rateReading(reading_session_id, rating, session_id, { via = '' } = {}) {
  const response = await axios.post(static_store.apiUrl + '/reader/rate-reading/', {
    reading_session_id, rating, session_id
  });
  // via 'gift': a package rated in its room (recipient journey R4.31).
  track('reading_rated', { item_id: reading_session_id, rating, ...(via ? { via } : {}) });
  return response.data;
}

// Redeem a single-use invite at a shared reading's summary gate. Pays the
// referral (links friend + Quest "Referral") once, only for a verified,
// ToS-accepting, NEW seeker; unmet conditions PEND (safe to re-call after
// verification). `inv` is the invite token from the magic link's ?inv=.
// Returns { captured }.
async function captureReferral(inv, session_id, tos_accepted) {
  const response = await axios.post(static_store.apiUrl + '/reader/capture-referral/', {
    inv, session_id, tos_accepted
  });
  return response.data;
}

// Mint a single-use invite per recipient and deliver its magic link via
// Email/SMS (ACS). The BE builds the per-recipient /s/<short>/?inv=<token> link
// from the reading's short shared uuid. recipients: [{ type:'email'|'phone', value }].
// Returns { sent: [...] }.
async function sendInvite(reading_session_id, recipients, sharer_name, session_id) {
  const response = await axios.post(static_store.apiUrl + '/reader/send-invite/', {
    reading_session_id, recipients, sharer_name, session_id
  });
  return response.data;
}

// ── Care Package: a reading done FOR someone, sent to them ──────────────────
// Spec: https://claude.ai/artifact/JgiptzEg41x3wAVTS5Gfjr
// Each route is spelled out in full so the prod-route gate (scripts/
// check_prod_routes.sh) can probe the real endpoint rather than a placeholder.
const _post = (url, body) => axios.post(url, body).then(r => r.data)
// The toggle: who this reading is for. Empty name = for me.
// inv: her reading for someone started from the gift room carries the package
// (recipient journey R7.4); the server sets it as the reading's parent_invite.
async function careIntent(session_id, { for_name = '', for_querent_uuid = '', signed_as, inv = '' } = {}) {
  return _post(`${static_store.apiUrl}/reader/care-package/intent/`,
    { session_id, for_name, for_querent_uuid, ...(signed_as ? { signed_as } : {}), ...(inv ? { inv } : {}) })
}
// Fix either name (or the note) from the share screen; every text follows.
async function careRename(session_id, reading_session_id, fields) {
  return _post(`${static_store.apiUrl}/reader/care-package/rename/`, { session_id, reading_session_id, ...fields })
}
// The Mystic drafts the sender's one line when the share screen opens.
async function careDraft(session_id, reading_session_id) {
  return _post(`${static_store.apiUrl}/reader/care-package/draft/`, { session_id, reading_session_id })
}
// The send screen opens (2026-10-21, R4.6): her version and the Mystic's first
// message to her are written now (each once), so the sender can read exactly
// what she receives. { letter, first_message, ...the share payload }. No invite.
async function carePreview(session_id, reading_session_id) {
  return _post(`${static_store.apiUrl}/reader/care-package/preview/`, { session_id, reading_session_id })
}
// Account > Notifications (2026-10-21): "Email me when something arrives",
// the address's place on the opt-out list. { on, locked, reason, since, source }.
async function getNotifications(session_id) {
  const r = await axios.get(`${static_store.apiUrl}/reader/account/notifications/`, { params: { session_id } })
  return r.data
}
async function setNotifications(session_id, on) {
  return _post(`${static_store.apiUrl}/reader/account/notifications/`, { session_id, on: !!on })
}
// Texting (2026-10-31): one tick per event and channel ('email' | 'text').
// Text on needs a confirmed phone (400 phone_needed). Answers the payload.
async function setNotification(session_id, event, channel, on) {
  return _post(`${static_store.apiUrl}/reader/account/notifications/`, { session_id, event, channel, on: !!on })
}
// Account > How to reach you: a phone is added by a texted code, never without
// one. 409 phone_taken: confirmed on another account. { ok:false,
// reason:'sms_unavailable' } while texting is off.
async function addPhone(session_id, phone) {
  return _post(`${static_store.apiUrl}/reader/account/phone/`, { session_id, phone })
}
async function confirmPhone(session_id, code) {
  return _post(`${static_store.apiUrl}/reader/account/phone/confirm/`, { session_id, code })
}
async function removePhone(session_id) {
  return _post(`${static_store.apiUrl}/reader/account/phone/remove/`, { session_id })
}
// Send it. contact only when the name was typed; the server routes.
// No reminder switch (2026-10-19): one reminder about day 3 is part of the
// system, so the app never sends followups_enabled.
// No birthday (2026-10-19, Chris): the send screen no longer asks for hers;
// the server still accepts recipient_birthday from old clients.
// "♡ Ask {Name} to join my Circle" (mystic-page API.md §9): ask_circle only when ticked.
async function careSend(session_id, reading_session_id, { contact = '', note = '', signed_as, ask_circle = false } = {}) {
  return _post(`${static_store.apiUrl}/reader/care-package/send/`,
    { session_id, reading_session_id, contact, note, ...(signed_as ? { signed_as } : {}), ...(ask_circle ? { ask_circle: true } : {}) })
}
// The send screen asks before a package is made for a contact (R10.8):
// { blocked, line }. "This address has asked us not to send."
// With the gift being sent (mystic-page API.md §9) it also answers
// new_person and on_us ({ line, sub, carats }, only when the send is free).
async function careContactCheck(session_id, contact, reading_session_id = '') {
  return _post(`${static_store.apiUrl}/reader/care-package/contact-check/`,
    { session_id, contact, ...(reading_session_id ? { reading_session_id } : {}) })
}
// The short gift link, made when the send screen opens and BEFORE the tap, so
// the tap can share or copy inside its own gesture (iPhone Safari drops a
// share started after a slow request): { link, first_line, handed }. Makes no
// invite: nothing is sent until careLink (2026-10-19).
// note: the sender's line, committed to the prepared link as it is written; the
// server judges a changed one (400 'unkind', field 'note') and stores a kind one.
async function carePrepareLink(session_id, reading_session_id, { note } = {}) {
  return _post(`${static_store.apiUrl}/reader/care-package/link/prepare/`,
    { session_id, reading_session_id, ...(note !== undefined ? { note } : {}) })
}
// The hand-over, after the tap shared or copied the prepared link: the server
// saves the line, writes the close, and returns { link, message } (the same
// short link, and the words to paste). Same link each time until someone takes it.
// quick: make the invite and answer at once, no model call (the send screen
// sends it beside the full call, so the page hears "sent" even if it closes).
async function careLink(session_id, reading_session_id, { note, signed_as, contact, quick } = {}) {
  return _post(`${static_store.apiUrl}/reader/care-package/link/`,
    { session_id, reading_session_id, ...(note !== undefined ? { note } : {}), ...(signed_as ? { signed_as } : {}),
      ...(contact ? { contact } : {}), ...(quick ? { quick: true } : {}) })
}
async function careRevoke(session_id, reading_session_id) {
  return _post(`${static_store.apiUrl}/reader/care-package/revoke/`, { session_id, reading_session_id })
}
// The recipient's door: the sign-up. No auth; the invite token is the identity.
// A copied link's door asks nothing: her first act makes her guest place on
// this device (recipient journey R4.34). `email` is the pre-v1.3 code door.
async function careDoor(inv, { email } = {}) {
  // A signed-in member sends their session: the door is the room, not a sign-up.
  const r = await _post(`${static_store.apiUrl}/reader/care-package/door/`,
    { inv, tos_accepted: true, ...(email ? { email } : {}), ...(store.session_id ? { session_id: store.session_id } : {}),
      ...withDevice() })
  // A token without `existing` is a new member made at the door.
  if (r && r.token && !r.existing) {
    setUser(r.querent_id)
    track('sign_up', { method: r.guest ? 'care_package_link' : 'care_package' })
  }
  return r
}
// The same door, for someone who already has a place here: the server mailed a
// six-digit code to the address on the account, and this is them typing it back.
// It answers with the same payload the new-account door does.
// The code typed on the landing, or the token carried by the emailed link
// (?signin=<token>); either one signs the existing member in through the invite.
async function careSignin(inv, { code = '', token = '', newAccount = false } = {}) {
  // The room session goes along: a rating made there signed out reaches the
  // sender once she is in (R4.19).
  const r = await _post(`${static_store.apiUrl}/reader/care-package/signin/`,
    { inv, ...(code ? { code } : {}), ...(token ? { token } : {}), ...(store.session_id ? { session_id: store.session_id } : {}) })
  if (r && r.token) {
    setUser(r.querent_id)
    // A copied link's first opener: the code made the account. The door says
    // existing:false to everyone (texting hole D), so the server's
    // new_account decides; an older server has only the door's word.
    const made = typeof r.new_account === 'boolean' ? r.new_account : newAccount
    if (made) track('sign_up', { method: 'care_package_link' })
    else track('login', { method: 'care_package' })
  }
  return r
}
// Signed in: the session the server ties to this account (start-session with
// the token reuses this member's own session with that Mystic). Only the id;
// the page's own start_session does the rest when it runs.
async function careOwnSession({ subdomain, session_id, querent_id, token } = {}) {
  const r = await axios.get(`${static_store.apiUrl}/reader/start-session/`, {
    params: { subdomain: subdomain || 'iam', session_id: session_id || '', querent_id: querent_id || '', token: token || '' },
  })
  return (r && r.data && r.data.session_id) || ''
}
// The landing: the full reading for the invite holder (no auth needed). Same
// endpoint as getReadingPreview, with the invite (and the viewer's session).
// device: the account last signed in on this browser (kept through a sign-out):
// the sender's own browser, signed out, is the sender's view (R4.35).
async function careLanding(shortUuid, inv, session_id, device = '') {
  const sid = `?inv=${encodeURIComponent(inv)}` + (session_id ? `&session_id=${encodeURIComponent(session_id)}` : '')
    + (device ? `&device=${encodeURIComponent(device)}` : '')
  const r = await axios.get(`${static_store.apiUrl}/reader/reading-preview/${shortUuid}/${sid}`)
  return r.data
}
// "What does this mean for me?": start (or resume) her one clarifying card.
// { deeper: 'open', reading_session_id, exclude_card_ids, inherited_cards } |
// { deeper: 'drawn', reading_session_id }.
// carryTopic (the room's "Pull a card for where you stand", R4.27): no
// question first; the Mystic carries the topic and the picker opens at once.
async function careDeeper(inv, session_id, { carryTopic = false } = {}) {
  const r = await _post(`${static_store.apiUrl}/reader/care-package/deeper/`,
    { inv, session_id, ...(carryTopic ? { carry_topic: true } : {}) })
  track('gift_deeper_start', { state: (r && r.deeper) || '' })
  return r
}
// The gift room closed with its ✕ (walk 2026-10-29): a session still on the
// gift's draw goes to the Mystic's home, so a line typed there is never the
// card's question. Nothing is streamed. { left }.
async function careLeave(session_id) {
  return _post(`${static_store.apiUrl}/reader/care-package/leave/`, { session_id })
}
// The Mystic speaks first in the gift room (R4.23, R4.24): after she rates
// (rating 1 to 5), or after she has read to the end (no rating). The line and
// its branches arrive over her socket. { opened, rating, branches }.
async function careOpener(inv, session_id, rating = null) {
  return _post(`${static_store.apiUrl}/reader/care-package/opener/`,
    { inv, session_id, ...(rating ? { rating } : {}) })
}
// "Write back to your son" (2026-10-22 §7): her line and her rating (1 to 5,
// optional) go to the sender, once. The sender's message names the word.
async function careThank(inv, session_id, note = '', { rating = 0 } = {}) {
  const r = await _post(`${static_store.apiUrl}/reader/care-package/thank/`,
    { inv, session_id, note, ...(rating ? { rating } : {}) })
  track('gift_thanked', { with_note: !!(note && note.trim()) })
  return r
}
// "Add <sender> to your circle?" (2026-10-26): her answer in the gift room,
// once per package. Yes makes the friendship both ways (Outer Circle) and
// tells the sender, from her. { added }.
async function careCircle(inv, session_id, add) {
  return _post(`${static_store.apiUrl}/reader/care-package/circle/`, { inv, session_id, add: !!add })
}
// A lesson's arrival, signed out (community-lesson API.md §5): where the app
// would show the door, the gift room's guest place instead (no email, code or
// password); "Keep this" follows. { token, querent_id, session_id, querent_name,
// carat_balance, reader_subdomain, guest, keep }.
async function lessonGuestPlace(session_id) {
  return _post(`${static_store.apiUrl}/reader/lesson/guest-place/`, { session_id })
}
// Every newcomer door's guest place, at the draw only (navigation API §7):
// { token, querent_id, session_id, querent_name, reader_subdomain, guest,
// keep, nav }. 409 signed_in, 403 not_in_a_reading (no account from navigation).
// ── The Mystic page set (Claude outputs/mystic-page-2026-10-29/API.md) ──
// §6 Keep lives with the Journal's calls (keepImage, journalUnkeep).
// §5 "Today from the Mystics": only drops that exist; Your Mystics first.
async function getMysticDrops(session_id, { limit = 24, date } = {}) {
  const r = await axios.get(`${static_store.apiUrl}/reader/mystic-drops/`, { params: { session_id, limit, date } })
  return r.data
}
// §2 the Studio's "Your front door": the address, the QR text and the counts.
async function getStudioFrontDoor() {
  try {
    const r = await axios.get(`${static_store.apiUrl}/reader/studio/front-door/`, { headers: _authHeaders() })
    return r.data
  } catch (e) {
    return null
  }
}
// §3 an arrival through a Mystic's link (once per page load).
async function mysticPageArrive({ sub, device_id, session_id, via = 'link' } = {}) {
  return _post(`${static_store.apiUrl}/reader/mystic-page/arrive/`, { sub, device_id, session_id, via })
}
// §4 the device's free first reading, before a reading starts.
async function getFirstReading({ device_id, device_hint, session_id } = {}) {
  const r = await axios.get(`${static_store.apiUrl}/reader/first-reading/`, { params: { device_id, device_hint, session_id } })
  return r.data
}
async function guestPlace(session_id, door = {}) {
  return _post(`${static_store.apiUrl}/reader/guest-place/`, { session_id, ...door })
}
// "Keep this" (recipient journey R5.10): a guest place gives her email; one
// link is mailed to it. { sent, message }.
async function careKeep(session_id, email) {
  const r = await _post(`${static_store.apiUrl}/reader/care-package/keep/`, { session_id, email })
  track('gift_keep', { step: 'sent' })
  return r
}
// Stage 4 (recipient journey R7.1 to R7.3): the Mystic's closing turn after
// You & <sender>: { line, people: [{ label, for_name, name, role }], someone_else }.
// With text (her line after "Someone else"): { person }.
async function carePeople(inv, text) {
  return _post(`${static_store.apiUrl}/reader/care-package/people/`,
    { session_id: store.session_id, inv, ...(text !== undefined ? { text } : {}) })
}
// The end of her introduction (recipient journey R8.3, R8.4): the server
// says the Mystic's one line once and records it. needCarats: she reached for
// the store in the room, which is the balance trigger (R8.8).
// { introduction, moment: { trigger, line, link } | null }.
async function introductionMoment(session_id, { needCarats = false } = {}) {
  return _post(`${static_store.apiUrl}/reader/introduction/moment/`,
    { session_id, ...(needCarats ? { need_carats: true } : {}) })
}
// The Mystic recommends another voice in the room (recipient journey R9.4):
// her own typed line, and the package she is in. { recommendation: {line,
// link, sub_domain, name, practice} | null, surfaces }.
async function roomRecommend(session_id, text, inv = '') {
  return _post(`${static_store.apiUrl}/reader/room/recommend/`,
    { session_id, text, ...(inv ? { inv } : {}) })
}
// Her first send (R7.11): the password, in the same step. A guest gives her
// email too; it waits for the confirming link. { saved, pending, message? }.
async function carePassword(session_id, { password, email = '' } = {}) {
  const r = await _post(`${static_store.apiUrl}/reader/care-package/password/`,
    { session_id, password, ...(email ? { email } : {}) })
  track('gift_password', { step: r && r.pending ? 'pending' : 'saved' })
  return r
}
// The link in that mail (?keep=<token>, R5.11): her address is hers now, or
// the gift moved into the account that had it. Signs this device in:
// { token, querent_id, session_id, reader_subdomain, kept, reading, inv }.
async function careKeepConfirm(token, session_id = '') {
  const r = await _post(`${static_store.apiUrl}/reader/care-package/keep/confirm/`,
    { token, ...(session_id ? { session_id } : {}) })
  if (r && r.token) {
    setUser(r.querent_id)
    track('gift_keep', { step: r.kept === 'merged' ? 'merged' : 'confirmed' })
  }
  return r
}
// "What landed?" (R5.4): her one line, kept on her own reading.
async function readingReflection(session_id, reading_id, text) {
  const r = await _post(`${static_store.apiUrl}/reader/reading-reflection/`, { session_id, reading_id, text })
  track('reading_reflection')
  return r
}
// Talk it through: "Keep talking: 5 more" (POST /reader/talk/more/). The
// server charges the Mystic's one-card price once per round (a repeat of the
// same round charges nothing). Answers { ok, status, talk, carat_balance,
// charged, amount_required }; a 402 is "not enough carats" (the store opens).
async function talkMore(session_id, reading_id, round) {
  try {
    const data = await _post(`${static_store.apiUrl}/reader/talk/more/`, { session_id, reading_id, round })
    return { ok: true, status: 200, ...data }
  } catch (e) {
    const res = e && e.response
    if (res) return { ok: false, status: res.status, ...(res.data || {}) }
    throw e
  }
}
async function careReceived(session_id) {
  const r = await axios.get(`${static_store.apiUrl}/reader/care-package/received/`, { params: { session_id } })
  return r.data.received || []
}
async function careSent(session_id) {
  const r = await axios.get(`${static_store.apiUrl}/reader/care-package/sent/`, { params: { session_id } })
  return r.data.sent || []
}

// Record that the seeker accepted the Terms of Service + Privacy Policy at the
// registration consent gate. Idempotent. Returns { accepted: bool }.
async function acceptTos(session_id) {
  const response = await axios.post(static_store.apiUrl + '/reader/accept-tos/', { session_id });
  return response.data;
}

// Send a friend request (seeker → seeker). Returns { ok, status }.
async function sendFriendRequest(target_querent_uuid, session_id) {
  const response = await axios.post(static_store.apiUrl + '/reader/friend-request/', {
    target_querent_uuid, session_id
  });
  track('friend_request_sent');
  return response.data;
}

// Respond to an incoming friend request: action 'accept' (optional alias) | 'reject'.
async function respondFriendRequest(requester_querent_uuid, action, session_id, alias, tier) {
  const response = await axios.post(static_store.apiUrl + '/reader/respond-friend-request/', {
    requester_querent_uuid, action, alias, tier, session_id
  });
  if (action === 'accept') track('friend_request_accepted');
  return response.data;
}

// Move an existing friend between 'outer' and 'coven' (the Mirror's Circle tab).
// The coven holds 13: a full one answers { ok: false, reason: 'coven_full' }.
// `step_back_querent_uuid` is the swap in one call (both or neither). A
// success answers the roster ({ friends, coven_max }).
async function setFriendTier(friend_querent_uuid, tier, session_id, step_back_querent_uuid) {
  const body = { friend_querent_uuid, tier, session_id }
  if (step_back_querent_uuid) body.step_back_querent_uuid = step_back_querent_uuid
  const response = await axios.post(static_store.apiUrl + '/reader/set-friend-tier/', body);
  return response.data;
}

// Incoming pending friend requests (the Messages-tab inbox). Returns { requests: [...] }.
async function getFriendRequests(session_id) {
  const response = await axios.get(static_store.apiUrl + '/reader/friend-requests/', { params: { session_id } });
  return response.data;
}

// Share a reading directly with selected accepted friends (delivered as messages).
async function shareWithFriends(reading_session_id, friend_uuids, session_id, note) {
  const response = await axios.post(static_store.apiUrl + '/reader/share-with-friends/', {
    reading_session_id, friend_uuids, note, session_id
  });
  return response.data;
}

// DM list (conversations) for the Messages tab. Returns { conversations: [...] }.
async function getConversations(session_id) {
  const response = await axios.get(static_store.apiUrl + '/reader/conversations/', { params: { session_id } });
  return response.data;
}

// End-of-day journal summaries keyed by date (YYYY-MM-DD). Returns { journals: {...} }.
async function getDayJournals(session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/day-journals/', { params: { session_id, ..._ju() } });
  return _jt(r.data);
}

// Set the seeker's day-journal length target (words). Returns { ok, word_count }.
async function setJournalLength(session_id, word_count) {
  const r = await axios.post(static_store.apiUrl + '/reader/journal-length/', { session_id, word_count, ..._ju() });
  return _jt(r.data);
}

// Save a day's journal entry in the seeker's own words (verbatim, private).
async function journalSpoken(session_id, date, text) {
  const r = await axios.post(static_store.apiUrl + '/reader/journal/spoken/', { session_id, date, text, ..._ju() });
  return _jt(r.data);
}

// Aida's reflective response to a highlighted passage (lens) or a free question.
// Returns { ok, reflection } for the chat — never edits the entry.
async function journalReflect(session_id, date, { selection = '', lens = 'go_deeper', question = '' } = {}) {
  const r = await axios.post(static_store.apiUrl + '/reader/journal/reflect/', { session_id, date, selection, lens, question, ..._ju() });
  track('journal_reflect', { lens });
  return _jt(r.data);
}

// Archived drafts of a day's entry. Returns { ok, versions: [...] }.
async function journalVersions(session_id, date) {
  const r = await axios.get(static_store.apiUrl + '/reader/journal/versions/', { params: { session_id, date, ..._ju() } });
  return _jt(r.data);
}

// Restore an archived draft as the live entry. Returns { ok, date, journal }.
async function journalRestore(session_id, date, version_id) {
  const r = await axios.post(static_store.apiUrl + '/reader/journal/restore/', { session_id, date, version_id, ..._ju() });
  return _jt(r.data);
}

// Undo/redo: set the live entry to a version's content (no new draft). { ok, journal }.
async function journalSetVersion(session_id, date, version_id) {
  const r = await axios.post(static_store.apiUrl + '/reader/journal/set-version/', { session_id, date, version_id, ..._ju() });
  return _jt(r.data);
}

// Consolidate: reorganize the entry (seed + appended pieces) into one cohesive entry.
async function journalConsolidate(session_id, date) {
  const r = await axios.post(static_store.apiUrl + '/reader/journal/consolidate/', { session_id, date, ..._ju() });
  return _jt(r.data);
}

// ── The Journal as a book (journal redesign 2026-10-27) ──────────────────── //
// Contract: Claude outputs/journal-redesign-2026-10-27/API.md (v1.1). Calls
// marked 🔒 there carry the clasp's unlock token while it is open, and every
// success answers with a refreshed one (kept in memory only). A 4xx with a
// JSON reason (423 locked, 403 wrong_pin, 429 too_many …) is returned, not
// thrown, so the book can switch on `reason`.
const _ju = () => (clasp.token ? { unlock_token: clasp.token } : {})
function _jt(data) { if (data && data.unlock_token) openClasp(data.unlock_token); return data }
function _jerr(e) {
  const d = e && e.response && e.response.data
  if (d && typeof d === 'object' && d.reason) return { ok: false, status: e.response.status, ...d }
  throw e
}
async function _jget(path, params) {
  try { const r = await axios.get(static_store.apiUrl + path, { params: { ...params, ..._ju() } }); return _jt(r.data) } catch (e) { return _jerr(e) }
}
async function _jpost(path, body) {
  try { const r = await axios.post(static_store.apiUrl + path, { ...body, ..._ju() }); return _jt(r.data) } catch (e) { return _jerr(e) }
}
function _deviceTz() { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch (e) { return '' } }
function _journalTz() { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch (e) { return 'UTC' } }

// Not 🔒: { has_journal, unseen, unseen_date, lock: {on, pin, passkey, offered}, cover_opened }.
const getJournalStatus = (session_id) => _jget('/reader/journal/status/', { session_id })
// 🔒 Newest day first; `before` pages back (next_before). `tz` stores the seeker's zone.
const getJournalDays = (session_id, { before = '', limit = 31 } = {}) =>
  _jget('/reader/journal/days/', { session_id, tz: _journalTz(), limit, ...(before ? { before } : {}) })
// 🔒 One page: { day } (null: nothing on that date).
const getJournalDay = (session_id, date) => _jget('/reader/journal/day/', { session_id, date })
// 🔒 "Did it hold?" held | partly | not → { answer, entry }.
async function journalAnswer(session_id, date, answer) {
  const d = await _jpost('/reader/journal/intention/', { session_id, date, answer })
  if (d && d.ok) track('journal_intention_answer', { answer })
  return d
}
// 🔒 One line / one page: hide | unhide | delete (the journal's copy only).
const journalItem = (session_id, date, item_id, action) => _jpost('/reader/journal/item/', { session_id, date, item_id, action })
const journalPage = (session_id, date, action) => _jpost('/reader/journal/page/', { session_id, date, action })
// The new entry was read (the gold dot goes out). No date: every entry.
const journalSeen = (session_id, date) => _jpost('/reader/journal/seen/', { session_id, ...(date ? { date } : {}) })
// Once-per-person book moments: 'cover' (its first opening), 'clasp_offer' (the slip shown).
const journalOnce = (session_id, key) => _jpost('/reader/journal/moment/', { session_id, key })
// The clasp: a PIN turns it on (or changes it; with a reset_token after Forgot).
async function journalSetPin(session_id, pin, { reset_token = '' } = {}) {
  const d = await _jpost('/reader/journal/lock/pin/', { session_id, pin, ...(reset_token ? { reset_token } : {}) })
  if (d && d.ok && !reset_token) track('journal_clasp_on', { passkey: 0 })
  return d
}
const journalUnlock = (session_id, pin) => _jpost('/reader/journal/lock/unlock/', { session_id, pin })
const journalLockRefresh = (session_id) => _jpost('/reader/journal/lock/refresh/', { session_id })
const journalLockOff = (session_id) => _jpost('/reader/journal/lock/off/', { session_id })
// Passkeys (Face ID / Touch ID): register on top of the PIN, and open with one.
const journalPasskeyRegisterOptions = (session_id) => _jpost('/reader/journal/lock/passkey/register/options/', { session_id })
async function journalPasskeyRegister(session_id, credential) {
  const d = await _jpost('/reader/journal/lock/passkey/register/', { session_id, credential })
  if (d && d.ok) track('journal_clasp_on', { passkey: 1 })
  return d
}
const journalPasskeyOptions = (session_id) => _jpost('/reader/journal/lock/passkey/options/', { session_id })
const journalPasskeyVerify = (session_id, credential) => _jpost('/reader/journal/lock/passkey/verify/', { session_id, credential })
// Forgot the PIN: a six-digit sign-in code by email → a reset_token → a new PIN.
const journalForgot = (session_id) => _jpost('/reader/journal/lock/forgot/', { session_id })
const journalForgotVerify = (session_id, code) => _jpost('/reader/journal/lock/forgot/verify/', { session_id, code })

// ── The Journal as a practice (2026-10-28) ─────────────────────────────────── //
// Contract: Claude outputs/journal-practice-2026-10-28/API.md (v1). All 🔒 like
// the book's other calls (the clasp's token rides along, reasons come back).
// Thread ids are integers; they go back exactly as they came.
const _has = (x) => x !== null && x !== undefined && x !== ''
// §5 One of their own lines: piece = gut | landed | meaning. → { ok, day } (the whole day). Empty text clears it.
const journalLine = (session_id, date, piece, text, { reading_id = '', thread_id = null } = {}) =>
  _jpost('/reader/journal/line/', { session_id, date, piece, text, ...(reading_id ? { reading_id } : {}), ...(_has(thread_id) ? { thread_id } : {}) })
// §4 Every thread, newest activity first ("Or add: …", merge). → { ok, threads }.
// Kept (mystic page 2026-10-29, Claude outputs/mystic-page-2026-10-29/API.md §6): 🔒 the shelf, newest first →
// { ok, count, line, preview, items: [{ id, kept_at, date, source, title, caption, image_url, line, giver }], you_and }.
const getJournalKept = (session_id) => _jget('/reader/journal/kept/', { session_id })
const getJournalThreads = (session_id) => _jget('/reader/journal/threads/', { session_id })
// §4 A thread's page. → { ok, thread, arc, steps } · 404 not_found (with merged_into when merged away).
const getJournalThread = (session_id, id) => _jget(`/reader/journal/thread/${encodeURIComponent(id)}/`, { session_id })
const journalThreadRename = (session_id, thread_id, name) => _jpost('/reader/journal/thread/rename/', { session_id, thread_id, name })
const journalThreadMerge = (session_id, thread_id, into_id) => _jpost('/reader/journal/thread/merge/', { session_id, thread_id, into_id })
// A day's tags: an existing thread (thread_id) or a new one (name). → { ok, date, threads, thread }. Untag is this day only.
const journalThreadTag = (session_id, date, { thread_id = null, name = '' } = {}) =>
  _jpost('/reader/journal/thread/tag/', { session_id, date, ...(_has(thread_id) ? { thread_id } : { name }) })
const journalThreadUntag = (session_id, date, thread_id) => _jpost('/reader/journal/thread/untag/', { session_id, date, thread_id })
// §3 The month's calendar; the practice reads its `chapter` state for the line under the left page's list.
const getJournalCalendar = (session_id, month) => _jget('/reader/journal/calendar/', { session_id, month })
// §6 A month's chapter: { ok, chapter | null }. Saving their text (plain, ≤5000) makes it theirs; empty text returns to the sections.
const getJournalChapter = (session_id, month) => _jget('/reader/journal/chapter/', { session_id, month })
const journalChapterSave = (session_id, month, text) => _jpost('/reader/journal/chapter/', { session_id, month, text })
// The seeker opened their horoscope (API.md §6): the Journal's "Read your horoscope" line.
async function horoscopeOpened(session_id, date = '') {
  try { const r = await axios.post(static_store.apiUrl + '/reader/personal-horoscope/opened/', { session_id, ...(date ? { date } : {}) }); return r.data } catch (e) { return null }
}

// Aggregate notification count for the Messages icon (unread DMs + pending friend
// requests). Returns { total, unread_messages, pending_requests }.
async function getMessageCount(session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/message-count/', { params: { session_id } });
  return r.data;
}

// Full message thread with one friend. Returns { messages: [...], friend_nick_name }.
async function getMessageThread(other_querent_uuid, session_id) {
  const response = await axios.get(
    static_store.apiUrl + `/reader/message-thread/${other_querent_uuid}/`, { params: { session_id } });
  return response.data;
}

// Send a DM to a friend (optionally attach a reading). Returns { ok }.
async function sendMessage(to_querent_uuid, body, session_id, reading_session_id) {
  const response = await axios.post(static_store.apiUrl + '/reader/send-message/', {
    to_querent_uuid, body, reading_session_id, session_id
  });
  return response.data;
}

// The seeker's LOCAL date (YYYY-MM-DD) — sent so the day matches the user's
// timezone, not the server's UTC.
function clientDate() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// The seeker's personal daily horoscope (Altar Home). Returns
// { state: 'needs_birthdate' | 'ready' | 'reading', horoscope?: {...} }.
// One per LOCAL day: `tz` turns the day at the seeker's midnight (2026-10-30).
async function getPersonalHoroscope(session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/personal-horoscope/', {
    params: { session_id, client_date: clientDate(), tz: _journalTz() },
  });
  return r.data;
}

// Re-focus today's personalized horoscope (the settings form). word_count 10-255
// sets the length. Returns { ok, horoscope }.
async function customizePersonalHoroscope(session_id, focus, word_count) {
  const r = await axios.post(static_store.apiUrl + '/reader/personal-horoscope/customize/', {
    session_id, focus, word_count, client_date: clientDate(), tz: _journalTz(),
  });
  track('horoscope_personalized');
  return r.data;
}

// The seeker's affirmation — stays CONSTANT until changed. Returns
// { state: 'ready' | 'reading', affirmation?: {...}, archive: [...] }.
async function getPersonalAffirmation(session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/personal-affirmation/', {
    params: { session_id },
  });
  return r.data;
}

// Generate a NEW affirmation (becomes current; prior one archived). word_count 3-20.
// from_sky: the Altar's offer (altar redesign 2026-10-27), seeded on the server
// from today's horoscope; focus is then ignored.
async function customizePersonalAffirmation(session_id, focus, word_count, from_sky = false) {
  const r = await axios.post(static_store.apiUrl + '/reader/personal-affirmation/customize/', {
    session_id, focus, word_count, ...(from_sky ? { from_sky: true, client_date: clientDate() } : {}),
  });
  return r.data;
}

// The Altar's name for the same call (the Journal's horoscope item).
const markHoroscopeOpened = horoscopeOpened;

// The Altar's first-visit rite was walked or skipped: recorded once on the
// server so it never replays (altar redesign 2026-10-27). Returns { ok, rite }.
async function markAltarRiteDone(session_id) {
  const r = await axios.post(static_store.apiUrl + '/reader/altar-space/rite-done/', { session_id });
  return r.data;
}

// Save the seeker's OWN affirmation words verbatim (becomes current; prior archived).
async function setPersonalAffirmation(session_id, text) {
  const r = await axios.post(static_store.apiUrl + '/reader/personal-affirmation/set/', {
    session_id, text,
  });
  return r.data;
}

// Personal Altar — paint your belief system, generate/archive/select an altar
// image. First 2 generations free, then 50 ✦ each (mirrors altar_space.py).
// `subject` ('altar' | 'keeper') selects which image space to read/paint.
async function getAltarSpace(session_id, subject = 'altar') {
  const r = await axios.get(static_store.apiUrl + '/reader/altar-space/', { params: { session_id, subject } });
  return r.data;
}

// Save belief facets (no image generation). Returns the full altar-space payload.
async function saveAltarSpace(session_id, facets, subject = 'altar') {
  const r = await axios.post(static_store.apiUrl + '/reader/altar-space/save/', { session_id, facets, subject });
  return r.data;
}

// Generate an image from the saved/supplied beliefs. On insufficient
// emeralds the server returns HTTP 402 { reason: 'insufficient_carats', ... }.
async function generateAltarImage(session_id, facets, subject = 'altar') {
  try {
    const r = await axios.post(static_store.apiUrl + '/reader/altar-space/generate/', { session_id, facets, subject });
    return r.data;
  } catch (e) {
    if (e.response && e.response.status === 402) return e.response.data;
    throw e;
  }
}

// The seeker's Mirror profile — avatar, sun/rising signs, altar (+ share state),
// and shared readings.
// The About blurb is fetched separately from the Mirror payload: writing one is
// an LLM round trip and the Mirror is on the critical path of the page.
async function generateMirrorAbout(session_id) {
  const r = await axios.post(static_store.apiUrl + '/reader/mirror/about/', { session_id });
  return r.data;
}
async function shareMirrorAbout(session_id, is_public) {
  const r = await axios.post(static_store.apiUrl + '/reader/mirror/about/share/',
    { session_id, is_public });
  return r.data;
}

async function getMirrorProfile(session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/mirror/', { params: { session_id } });
  return r.data;
}

// Seeker evaluation: reveal (25 emeralds, gated at 10 readings), slant, reset, restore, share.
async function generateEvaluation(session_id) {
  const r = await axios.post(static_store.apiUrl + '/reader/evaluation/generate/', { session_id });
  return r.data;
}
async function slantEvaluation(session_id, instruction) {
  const r = await axios.post(static_store.apiUrl + '/reader/evaluation/slant/', { session_id, instruction });
  return r.data;
}
async function setEvaluationLength(session_id, words) {
  const r = await axios.post(static_store.apiUrl + '/reader/evaluation/length/', { session_id, words });
  return r.data;
}
async function restoreEvaluation(session_id, index) {
  const r = await axios.post(static_store.apiUrl + '/reader/evaluation/restore/', { session_id, index });
  return r.data;
}
async function shareEvaluation(session_id, is_public) {
  const r = await axios.post(static_store.apiUrl + '/reader/evaluation/share/', { session_id, is_public });
  return r.data;
}

// The seeker's full image gallery (avatars, altars, reading images) + favorites.
async function getImageGallery(session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/image-gallery/', { params: { session_id } });
  return r.data;
}

// Heart/unheart any image into the seeker's Favorites.
async function toggleFavoriteImage(session_id, image_url, { owner_uuid = '', source = '', title = '' } = {}) {
  const r = await axios.post(static_store.apiUrl + '/reader/image-gallery/favorite/', { session_id, image_url, owner_uuid, source, title });
  return r.data;
}

// Keep (mystic page 2026-10-29, API.md §6): one verb for images from others (a drop, a
// lesson picture, a painting, a card). `body` is the server's prefilled `keep` object
// ({ image_url, source, title, caption?, reader?, owner?, ref? }). → { kept: true, item };
// 403 { reason: 'own' } for your own image; 401 signed out. Idempotent.
async function keepImage(session_id, body = {}) {
  const r = await axios.post(static_store.apiUrl + '/reader/keep/', { ...body, session_id });
  return r.data;
}
// "Remove from Kept": off the shelf only (the day's Journal line stays). → { kept: false }.
async function journalUnkeep(session_id, id) {
  try { const r = await axios.post(static_store.apiUrl + '/reader/keep/remove/', { session_id, id }); return r.data } catch (e) { return _jerr(e) }
}

// Platform quests — the seeker's quest board (phases + daily/streak + become-mystic gate).
async function getPlatformQuests(session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/quests/', { params: { session_id } });
  return r.data;
}

// Claim a completed quest (credits emeralds once). Returns the refreshed board.
async function claimQuest(session_id, quest_key) {
  const r = await axios.post(static_store.apiUrl + '/reader/quests/claim/', { session_id, quest_key });
  track('quest_reward_claimed', { quest: quest_key });
  return r.data;
}

// "Claim N ✦" on the Quest sky (navigation API §4): every claimable star,
// wish, tick, the moon and the vote in one transaction. { ok, reason:
// 'claimed'|'nothing'|'not_yet', claimed: [keys], total, ..., sky }.
async function claimAllQuests(session_id) {
  const r = await axios.post(static_store.apiUrl + '/reader/quests/claim-all/', { session_id });
  if (r.data && r.data.ok && r.data.total) track('nav_claim_all', { total: r.data.total, count: (r.data.claimed || []).length });
  return r.data;
}

// Navigation (navigation API §2, §5): the server's `nav` on its own, a cheap
// refresh after an event; and a one-time moment seen (unlock:*, line:*,
// dot:community, quest, altar:journal, altar:mirror). Both return { nav }.
async function getNav(session_id, door = {}) {
  const r = await axios.get(static_store.apiUrl + '/reader/nav/', { params: { session_id, ...door } });
  return r.data;
}
async function navSeen(session_id, key) {
  const r = await axios.post(static_store.apiUrl + '/reader/nav/seen/', { session_id, key });
  return r.data;
}
// The Quest journey's acts with no home of their own (API v1 §4):
// birth_time_unknown, keep_sky, thank_mystic {reading_id, rating?, note?}.
// Answers {ok, act, nav, sky}.
async function questAct(session_id, act, extra = {}) {
  const r = await axios.post(static_store.apiUrl + '/reader/quest/act/', { session_id, act, ...extra });
  return r.data;
}

// Mystic seat — state plus what subscribing would cost at the current cohort
// rung. Charged in carats as an internal debit; there is no Stripe
// subscription behind it, so no card is collected here.
async function getMysticSeat(session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/mystic-seat/', { params: { session_id } });
  return r.data;
}

// Open a seat. Returns { ok, reason, cost, balance, seat }.
async function subscribeMysticSeat(session_id, plan = 'mystic') {
  const r = await axios.post(static_store.apiUrl + '/reader/mystic-seat/subscribe/', { session_id, plan });
  if (r.data && r.data.ok) track('mystic_seat_opened', { plan });
  return r.data;
}

// Acknowledge the Studio-unlocked celebration so it never fires again. Server
// side, so it does not replay on a new device or after a cache clear.
async function markMysticUnlockSeen(session_id) {
  const r = await axios.post(static_store.apiUrl + '/reader/quests/unlock-seen/', { session_id });
  return r.data;
}

// Permanently delete one of the seeker's own readings (cascades on the server).
async function deleteReadingSession(session_id, reading_uuid) {
  const r = await axios.post(static_store.apiUrl + '/reader/delete-reading/', { session_id, reading_uuid });
  return r.data;
}

// Save the querent's account (name/email/birth date+time+place). Used by Settings
// and the Mirror's birth-details modal. Requires querent_id + email + name.
async function saveQuerentAccount(payload) {
  const r = await axios.post(static_store.apiUrl + '/querent/manage-account/', payload);
  return r.data;
}

// ── The birth moment (one write path, Mystic-Aida tarot/birth_views.py) ────
// GET -> { moment, signs, windows, min_age }; POST a moment -> the same, or a
// 400 with { errors: { birthdate?, time?, place? } } (thrown, read e.response).
async function getBirthMoment() {
  const r = await axios.get(`${static_store.apiUrl}/birth/moment/`, { params: { session_id: store.session_id } })
  return r.data
}
async function saveBirthMoment(moment) {
  return _post(`${static_store.apiUrl}/birth/moment/`, { session_id: store.session_id, ...moment })
}
// Type-ahead: [{ name, lat, lng }].
async function searchBirthPlaces(q) {
  const r = await axios.get(`${static_store.apiUrl}/birth/places/`, { params: { q } })
  return (r.data && r.data.results) || []
}
// The sender's guess at the recipient's birthday, offered to her alone.
async function getSuggestedBirthday(inv) {
  const r = await axios.get(`${static_store.apiUrl}/birth/suggested/`, { params: { session_id: store.session_id, inv } })
  return r.data && r.data.suggested
}
async function confirmSuggestedBirthday(inv) {
  return _post(`${static_store.apiUrl}/birth/suggested/confirm/`, { session_id: store.session_id, inv })
}
// "What do Sam's stars say about us?" -> { status: needs_my_birth | waiting_for_sender | ready, ... }
async function ourStars(inv) {
  return _post(`${static_store.apiUrl}/birth/our-stars/`, { session_id: store.session_id, inv })
}
// Stage 3 (recipient journey R6.6): her sun sign in one line tied to the card
// she pulled. { sign, card, reversed, line }; 400 before she has a birthday.
async function signLine(inv) {
  return _post(`${static_store.apiUrl}/birth/sign-line/`, { session_id: store.session_id, inv })
}
// Stage 3 (R6.11, R6.12): { wrote_back, from_name, altar, has_birth_date, sign }.
async function careStageThree(inv) {
  const r = await axios.get(`${static_store.apiUrl}/reader/care-package/stage-three/`,
    { params: { session_id: store.session_id, inv } })
  return r.data
}
async function getBirthdayRequest(token) {
  const r = await axios.get(`${static_store.apiUrl}/birth/request/${encodeURIComponent(token)}/`)
  return r.data
}
async function answerBirthdayRequest(token, moment) {
  return _post(`${static_store.apiUrl}/birth/request/${encodeURIComponent(token)}/`, moment)
}
export {
  getBirthMoment, saveBirthMoment, searchBirthPlaces, getSuggestedBirthday, confirmSuggestedBirthday,
  ourStars, getBirthdayRequest, answerBirthdayRequest, signLine, careStageThree,
}

// Change the seeker's password (token-auth, requires the current password).
// dj-rest-auth's PasswordChangeView. Throws on a 400 (the caller surfaces the error).
async function changePassword(new_password1, new_password2, old_password) {
  const token = localStorage.getItem('authToken')
  const r = await axios.post(
    static_store.apiUrl + '/dj-rest-auth/password/change/',
    { new_password1, new_password2, old_password },
    token ? { headers: { Authorization: `Token ${token}` } } : undefined,
  )
  return r.data
}

// Permanently delete the seeker's account. Destructive + irreversible — the UI
// gates this behind an explicit type-to-confirm. Returns { ok }.
async function deleteQuerentAccount(session_id) {
  const token = localStorage.getItem('authToken')
  const r = await axios.post(
    static_store.apiUrl + '/querent/delete-account/',
    { session_id, confirm: 'DELETE' },
    token ? { headers: { Authorization: `Token ${token}` } } : undefined,
  )
  return r.data
}

// Share / unshare the altar on the Mirror profile. Returns { ok, is_public }.
async function setAltarPublic(session_id, is_public, subject = 'altar') {
  const r = await axios.post(static_store.apiUrl + '/reader/altar-space/share/', { session_id, is_public, subject });
  return r.data;
}

// Share / unshare the current affirmation on the Mirror profile. Returns { ok, is_public }.
async function setAffirmationPublic(session_id, is_public) {
  const r = await axios.post(static_store.apiUrl + '/reader/affirmation/share/', { session_id, is_public });
  return r.data;
}

// Build the Advanced prompt description from the Simple selections. Returns the
// full altar-space payload (incl. the new `prompt`).
async function compileAltarPrompt(session_id, facets, subject = 'altar') {
  const r = await axios.post(static_store.apiUrl + '/reader/altar-space/compile/', { session_id, facets, subject });
  return r.data;
}

// The assistant — returns { reply, suggestions } for a facet, or { reply, prompt }
// when field === 'prompt' (shapes the Advanced description).
async function assistAltarField(session_id, field, message, facets, subject = 'altar') {
  const r = await axios.post(static_store.apiUrl + '/reader/altar-space/assist/', {
    session_id, field, message, facets, subject,
  });
  return r.data;
}

// Make a previously generated image the live one. Returns the altar-space payload.
async function selectAltarImage(session_id, image_id, subject = 'altar') {
  const r = await axios.post(static_store.apiUrl + '/reader/altar-space/select/', { session_id, image_id, subject });
  return r.data;
}

// Follow / unfollow a Mystic. action 'follow' | 'unfollow'. Returns { following, followers }.
async function followReader(subdomain, action, session_id) {
  const response = await axios.post(static_store.apiUrl + '/reader/follow-reader/', {
    subdomain, action, session_id
  });
  track(action === 'unfollow' ? 'unfollow_reader' : 'follow_reader', { reader: subdomain });
  return response.data;
}

// The Mystics tab (2026-10-26): the whole page, the box, the order of Your
// Mystics, and the home Mystic. Backend: reader/views_mystics.py.
async function getMysticsTab(session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/mystics-tab/', { params: session_id ? { session_id } : {} });
  return r.data;
}
// params: { q } | { topic } | { vibe } | { name }, plus { suggest: 1 } while typing (word list only).
async function mysticMatch(params, session_id) {
  const r = await axios.get(static_store.apiUrl + '/reader/mystic-match/', {
    params: { ...params, ...(session_id ? { session_id } : {}) } });
  return r.data;
}
async function setFollowOrder(order, session_id) {
  const r = await axios.post(static_store.apiUrl + '/reader/follow-order/', { order, session_id });
  return r.data;
}
async function setHomeMystic(subdomain, session_id) {
  const r = await axios.post(static_store.apiUrl + '/reader/home-mystic/', { subdomain, session_id });
  track('home_mystic', { reader: subdomain });
  return r.data;
}

// The current seeker's friends (for the Share popup picker). Returns { friends: [...] }.
async function getMyFriends(session_id) {
  const response = await axios.get(static_store.apiUrl + '/reader/my-friends/', {
    params: { session_id }
  });
  return response.data;
}

// ─── Reader ─────────────────────────────────────────────────────── //

/** The Mystic's chart sheet, read back for the Studio. Same shape the birth used. */
async function getMyChart() {
  const token = localStorage.getItem('authToken')
  const headers = token ? { Authorization: `Token ${token}` } : {}
  return axios.get(`${static_store.apiUrl}/reader/my-chart/`, { headers, withCredentials: true })
}

async function getMyReader() {
  try {
    const token = localStorage.getItem('authToken')
    const headers = {}
    if (token) {
      headers['Authorization'] = `Token ${token}`
    }
    // quiet=1: "not a Mystic" comes back as a 204, not a 404, so a seeker's
    // every page load no longer logs a failed call (loop walk 2026-10-26 #2).
    // Callers keep the old contract: not a reader still throws a 404-shaped error.
    const response = await axios.get(`${static_store.apiUrl}/reader/my-reader/`, { headers, params: { quiet: 1 } });
    if (response.status === 204) {
      store.isReader = false;
      const none = new Error('Not a Mystic');
      none.response = { status: 404 };
      throw none;
    }
    store.reader = response.data;
    store.isReader = true;
    store.readerSkills = response.data.skills || [];
    return response;
  } catch (error) {
    if (error.response?.status === 404) {
      store.isReader = false;
    } else {
      console.error('Error fetching reader:', error);
    }
    throw error;
  }
}

// Superuser-only: roster of non-keeper readers for the Studio's "edit another
// reader" picker. Returns [] for non-admins (the BE 403s; we swallow it so the
// dropdown simply never appears).
async function getStudioReaders() {
  try {
    const token = localStorage.getItem('authToken')
    const headers = {}
    if (token) {
      headers['Authorization'] = `Token ${token}`
    }
    const response = await axios.get(`${static_store.apiUrl}/reader/studio/readers/`, { headers });
    return response.data || [];
  } catch (error) {
    if (error.response?.status !== 403) {
      console.error('Error fetching studio readers:', error);
    }
    return [];
  }
}

async function updateReader(payload) {
  try {
    const token = localStorage.getItem('authToken')
    const headers = {}
    if (token) {
      headers['Authorization'] = `Token ${token}`
    }
    const response = await axios.patch(`${static_store.apiUrl}/reader/update-reader/`, payload, { headers });
    Object.assign(store.reader, response.data);
    return response;
  } catch (error) {
    // A refusal of public text (Spec 2) is an answer the panel shows under the field.
    if (!profileFieldRejection(error)) console.error('Error updating reader:', error);
    throw error;
  }
}

// Studio Profile avatar picker — Browse tab. Hits the public
// portrait pool with hierarchical filters (era / theme / subject /
// gender). Same endpoint the become-mystic ImageStep uses, no auth.
async function browseStudioPortraits(filters = {}, page = 1, perPage = 9) {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  })
  for (const [k, v] of Object.entries(filters)) {
    if (v) params.set(k, v)
  }
  const r = await axios.get(
    `${static_store.apiUrl}/reader/become-mystic/browse-portraits/?${params}`,
  )
  return r.data
}

// Studio Profile avatar picker — Create tab. POST a prose
// description + count; BE fans out to N parallel gpt-image-1 gens,
// classifies each via vision, and inserts every result into
// ThemePortraitPool with is_claimed=False. Returns the array of
// portraits so the FE can show a triptych. Unpicked siblings
// remain browsable in the public pool.
async function generateStudioPortrait(description, count = 3) {
  const token = localStorage.getItem('authToken')
  const headers = {}
  if (token) headers['Authorization'] = `Token ${token}`
  // Wall-clock budget: gpt-image-2 low ~25s + classification ~5s +
  // possible launder-retry ~25s + Azure lane queueing under load
  // can comfortably reach 90-120s. 240s buffer covers tail-end cases
  // without making the operator wait forever for a hung request.
  const r = await axios.post(
    `${static_store.apiUrl}/reader/studio/generate-portrait/`,
    { description, count },
    { headers, timeout: 240000 },
  )
  return r.data
}

// Studio Profile avatar picker. FE posts the chosen URL (a pool
// portrait or a fresh-gen triptych pick); BE downloads it onto
// reader.background_image. When ``poolId`` is provided, BE also
// marks that ThemePortraitPool row as is_claimed=True so it stops
// showing in Browse.
async function setReaderAvatarFromUrl(imageUrl, poolId = null) {
  const token = localStorage.getItem('authToken')
  const headers = {}
  if (token) headers['Authorization'] = `Token ${token}`
  const body = { image_url: imageUrl }
  if (poolId) body.pool_id = poolId
  const r = await axios.post(
    `${static_store.apiUrl}/reader/set-avatar-from-url/`,
    body,
    { headers },
  )
  return r.data
}

// ─── Reader Onboarding ──────────────────────────────────────────── //

// ─── Altar ───────────────────────────────────────────────────────── //

async function getAltarGreeting(sessionId) {
  const token = localStorage.getItem('authToken')
  if (!token) return null
  try {
    const response = await axios.post(
      `${static_store.apiUrl}/reader/altar/`,
      { session_id: sessionId, token }
    )
    return response.data
  } catch (e) {
    console.error('Altar greeting failed:', e)
    return null
  }
}

// getCommunityGreeting RETIRED 2026-06-30 — the old Explore/Ritual/Shared
// greeting is gone (it polluted Mystic AIda's shared reader session with the
// COMMUNITY state, leaking onto her Doorman page). The Doorway greeting below
// (Meet-a-reader) is unrelated and still active.

async function getDoorwayGreeting(sessionId, readerSubdomain) {
  try {
    const response = await axios.post(
      `${static_store.apiUrl}/reader/doorway/`,
      { session_id: sessionId, reader_subdomain: readerSubdomain },
      { headers: { 'X-CSRFToken': getCsrfToken() } }
    )
    return response.data
  } catch (e) {
    console.error('Doorway greeting failed:', e)
    return null
  }
}

async function altarSetSign(sign, keeperSessionId) {
  const token = localStorage.getItem('authToken')
  if (!token) return null
  try {
    const response = await axios.post(
      `${static_store.apiUrl}/reader/altar/set-sign/`,
      { token, sign, keeper_session_id: keeperSessionId }
    )
    return response.data
  } catch (e) {
    console.error('Altar set sign failed:', e)
    return null
  }
}

async function getCommunityPulse(sessionId) {
  try {
    const params = sessionId ? `?session_id=${sessionId}` : ''
    const response = await axios.get(
      `${static_store.apiUrl}/reader/community-pulse/${params}`
    )
    return response.data
  } catch (e) {
    console.error('Community pulse failed:', e)
    return null
  }
}

async function getLatestDeck() {
  // Newest published deck (name, by-Mystic, a handful of Major Arcana fronts)
  // for the 'Just Arrived' living-deck widget on the Mystics hero.
  try {
    const response = await axios.get(`${static_store.apiUrl}/reader/latest-deck/`)
    return response.data?.deck || null
  } catch (e) {
    console.error('Latest deck fetch failed:', e)
    return null
  }
}

async function getMysticsVisited(session_id) {
  const token = localStorage.getItem('authToken')
  try {
    const params = new URLSearchParams({ session_id: session_id || '' })
    const r = await axios.get(
      `${static_store.apiUrl}/reader/mystics-visited/?${params.toString()}`,
      token ? { headers: { Authorization: `Token ${token}` } } : undefined,
    )
    return r.data?.mystics || []
  } catch (e) {
    console.error('Mystics visited fetch failed:', e)
    return []
  }
}

// Community Voice (feedback loop) polls — returns [{key, title, vote_mode,
// option_style, options:[{id,label,votes,pct,mine,...}], ...}].
async function getCommunityPolls(session_id) {
  try {
    const params = new URLSearchParams({ session_id: session_id || '' })
    const r = await axios.get(`${static_store.apiUrl}/community/polls/?${params.toString()}`)
    // Full envelope: the board ships paint_cost alongside the polls so the FE
    // never has to hardcode a price.
    return { polls: r.data?.polls || [], paint_cost: r.data?.paint_cost }
  } catch (e) {
    console.error('Community polls fetch failed:', e)
    return { polls: [], paint_cost: undefined, failed: true }
  }
}

// Cast/move (single) or toggle (upvote) a vote. Returns { poll } on success;
// throws with status 401 when the caller isn't a logged-in seeker.
async function castCommunityVote(poll_key, option_id, session_id) {
  const token = localStorage.getItem('authToken')
  const r = await axios.post(
    `${static_store.apiUrl}/community/polls/vote/`,
    { poll_key, option_id, session_id },
    token ? { headers: { Authorization: `Token ${token}` } } : undefined,
  )
  track('poll_vote', { poll: poll_key })
  return r.data
}

// FREE — paint a seasonal Mystic AIda draft. facets: {style, season, outfit,
// background, mood, prompt}. Returns { draft: {id, label, image_url} }.
async function generateCandidate(facets, session_id) {
  const token = localStorage.getItem('authToken')
  const r = await axios.post(
    `${static_store.apiUrl}/community/polls/generate/`,
    { facets, session_id },
    token ? { headers: { Authorization: `Token ${token}` } } : undefined,
  )
  track('candidate_generated')
  return r.data
}

// Submit a draft to the circle (20 ✦ = your single vote). Returns { poll, balance,
// option_id }; throws 401 (login) or 402 (insufficient carats).
async function submitCandidate(option_id, session_id) {
  const token = localStorage.getItem('authToken')
  const r = await axios.post(
    `${static_store.apiUrl}/community/polls/submit/`,
    { option_id, session_id },
    token ? { headers: { Authorization: `Token ${token}` } } : undefined,
  )
  track('candidate_submitted')
  return r.data
}

async function getMySubmission(session_id) {
  const token = localStorage.getItem('authToken')
  try {
    const params = new URLSearchParams({ session_id: session_id || '' })
    const r = await axios.get(
      `${static_store.apiUrl}/community/my-submission/?${params.toString()}`,
      token ? { headers: { Authorization: `Token ${token}` } } : undefined,
    )
    return r.data?.submission || null
  } catch (e) {
    return null
  }
}

async function getMyCandidates(session_id) {
  const token = localStorage.getItem('authToken')
  try {
    const params = new URLSearchParams({ session_id: session_id || '' })
    const r = await axios.get(
      `${static_store.apiUrl}/community/my-candidates/?${params.toString()}`,
      token ? { headers: { Authorization: `Token ${token}` } } : undefined,
    )
    return r.data?.candidates || []
  } catch (e) {
    return []
  }
}

async function deleteCandidate(option_id, session_id) {
  const token = localStorage.getItem('authToken')
  const r = await axios.post(
    `${static_store.apiUrl}/community/my-candidates/delete/`,
    { option_id, session_id },
    token ? { headers: { Authorization: `Token ${token}` } } : undefined,
  )
  return r.data
}

async function getDayDrops(session_id) {
  const token = localStorage.getItem('authToken')
  try {
    const params = new URLSearchParams({ session_id: session_id || '' })
    const r = await axios.get(
      `${static_store.apiUrl}/reader/day-drops/?${params.toString()}`,
      token ? { headers: { Authorization: `Token ${token}` } } : undefined,
    )
    return r.data?.drops || []
  } catch (e) {
    console.error('Day drops fetch failed:', e)
    return []
  }
}

// The seeker's personal daily horoscope per date — shown on the left page of each
// Journal day. Returns a { 'YYYY-MM-DD': {title, reading_text, focus, ...} } map.
async function getDayHoroscopes(session_id) {
  const token = localStorage.getItem('authToken')
  try {
    const params = new URLSearchParams({ session_id: session_id || '' })
    const r = await axios.get(
      `${static_store.apiUrl}/reader/day-horoscopes/?${params.toString()}`,
      token ? { headers: { Authorization: `Token ${token}` } } : undefined,
    )
    return r.data?.horoscopes || {}
  } catch (e) {
    console.error('Day horoscopes fetch failed:', e)
    return {}
  }
}

async function getDeckRating(subdomain, session_id) {
  try {
    const params = new URLSearchParams({ reader: subdomain || '', session_id: session_id || '' })
    const r = await axios.get(`${static_store.apiUrl}/reader/deck-rating/?${params.toString()}`)
    return r.data
  } catch (e) {
    console.error('Deck rating fetch failed:', e)
    return null
  }
}

async function rateDeck(subdomain, stars, session_id) {
  const token = localStorage.getItem('authToken')
  try {
    const r = await axios.post(
      `${static_store.apiUrl}/reader/deck-rating/`,
      { reader: subdomain, stars, session_id: session_id || '' },
      token ? { headers: { Authorization: `Token ${token}` } } : undefined,
    )
    return r.data
  } catch (e) {
    console.error('Deck rating failed:', e)
    return null
  }
}

async function getReaderDetail(subdomain) {
  // session_id lets the BE report is_following for the Follow button.
  // tz: the room's greeting is for the device's day and half of the world
  // (the holiday line, 2026-10-30; the horoscope's rule).
  const response = await axios.get(`${static_store.apiUrl}/reader/detail/${subdomain}/`, {
    params: { session_id: store.session_id || '', date: clientToday(), tz: _deviceTz() },
  })
  return response.data
}

// asLesson: the lesson page whoever asks, its sharer included (the Community's
// lesson card, 2026-10-25); the server answers it only for a public lesson.
async function getReadingPreview(shortUuid, { asLesson = false } = {}) {
  try {
    const params = new URLSearchParams()
    if (store.session_id) params.set('session_id', store.session_id)
    if (asLesson) params.set('as', 'lesson')
    const qs = params.toString() ? `?${params}` : ''
    const response = await axios.get(
      `${static_store.apiUrl}/reader/reading-preview/${shortUuid}/${qs}`
    )
    return response.data
  } catch (e) {
    console.error('Reading preview failed:', e)
    return null
  }
}

// More about the same topic, at the foot of a lesson (lesson-ending DESIGN.md
// §3, 2026-10-27): up to three other public lessons with its topic, as feed
// cards. { topic, topic_label, items }; null when it can't be had.
async function getLessonRelated(shortUuid) {
  if (!shortUuid) return null
  try {
    const params = new URLSearchParams()
    if (store.session_id) params.set('session_id', store.session_id)
    const qs = params.toString() ? `?${params}` : ''
    const response = await axios.get(`${static_store.apiUrl}/reader/lesson/related/${encodeURIComponent(shortUuid)}/${qs}`)
    return response.data
  } catch (e) {
    return null
  }
}

// The Community's Lessons stream (community-simplify build/API.md §2, §3): one
// endpoint for the newest-first stream, a topic chip (`topic`), a search by
// meaning (`q`, answered with a `search` object) and "spoke to the most
// people" (`sort: 'resonant'`). Returns { items, has_more, total, page, topics, search }.
async function getSeekerFeed(page = 1, { topic = '', q = '', sort = 'recent', pageSize = 12 } = {}) {
  try {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize), sort: sort || 'recent' })
    if (topic) params.set('topic', topic)
    if (q && String(q).trim()) params.set('q', String(q).trim())
    if (store.session_id) params.set('session_id', store.session_id)
    const response = await axios.get(`${static_store.apiUrl}/reader/seeker-feed/?${params.toString()}`)
    return response.data
  } catch (e) {
    console.error('Seeker feed failed:', e)
    return null
  }
}

/** A search of the lessons by meaning: the stream with `q` (API.md §3). */
function searchLessons(q, { topic = '', page = 1 } = {}) {
  return getSeekerFeed(page, { topic, q })
}

// ── Lesson search (lesson-redo API.md §1 to §3, §5) ──
// One search for lessons and Mystics: { q, topic, topic_label, how, line, count,
// page, lessons, has_more, mystics, ask }. null when it can't be had.
async function searchAll(q, { page = 1, pageSize = 9 } = {}) {
  try {
    const params = new URLSearchParams({ q: String(q || '').trim(), page: String(page), page_size: String(pageSize) })
    if (store.session_id) params.set('session_id', store.session_id)
    const response = await axios.get(`${static_store.apiUrl}/reader/search/?${params.toString()}`)
    return response.data
  } catch (e) {
    console.error('Search failed:', e)
    return null
  }
}
// The search log (§2): on Enter, a lesson tap or a Mystic tap, never per
// keystroke; the lesson's mark sends { q: '', kind: 'tap', sub_domain, source: 'lesson' }.
// Fire and forget: a failure never stops the tap.
async function logSearch({ q = '', kind = 'enter', sub_domain = '', source = '' } = {}) {
  try {
    const body = { q: String(q || '').trim(), kind }
    if (sub_domain) body.sub_domain = sub_domain
    if (source) body.source = source
    const response = await axios.post(`${static_store.apiUrl}/reader/search/log/`, body)
    return response.data
  } catch (e) {
    return null
  }
}
// The focused field's lines (§3): { asking, placeholders }.
async function getSearchSuggest() {
  try {
    const response = await axios.get(`${static_store.apiUrl}/reader/search/suggest/`)
    return response.data
  } catch (e) {
    return null
  }
}
// The Mystic's side (§5): this week in search, and "What I read for".
async function getFoundInSearch() {
  try {
    const response = await axios.get(`${static_store.apiUrl}/reader/studio/found-in-search/`, { headers: _authHeaders() })
    return response.data
  } catch (e) {
    return null
  }
}
async function setReadForTopics(topics) {
  try {
    const response = await axios.post(`${static_store.apiUrl}/reader/studio/read-for/`, { topics }, { headers: _authHeaders() })
    return response.data
  } catch (e) {
    const d = e && e.response && e.response.data
    return d ? { ...d, ok: false } : null
  }
}

// The Circle 'response layer' — offer a written grace back on a reading.
async function offerReadingGrace(shortUuid, text) {
  try {
    const response = await axios.post(`${static_store.apiUrl}/reader/offer-grace/`, {
      session_id: store.session_id, short_uuid: shortUuid, text,
    })
    return response.data
  } catch (e) {
    console.error('Offer grace failed:', e)
    return null
  }
}

// The Circle: send light / withdraw it for a shared reading. Returns {supported, support_count}.
async function toggleReadingSupport(shortUuid) {
  try {
    const response = await axios.post(`${static_store.apiUrl}/reader/reading-support/`, {
      session_id: store.session_id, short_uuid: shortUuid,
    })
    return response.data
  } catch (e) {
    console.error('Reading support failed:', e)
    return null
  }
}

// A seeker's public profile (for the global Seeker modal): stats, evaluation,
// shared readings, friend_status, sun sign. session_id computes friend_status.
async function getSeekerDetail(nickName, sessionId) {
  try {
    const response = await axios.get(
      `${static_store.apiUrl}/reader/seeker/${encodeURIComponent(nickName)}/`,
      { params: { session_id: sessionId || '' } }
    )
    return response.data
  } catch (e) {
    console.error('Seeker detail failed:', e)
    return null
  }
}

// Reveal (charging 5 emeralds the first time) the Sun+Rising compatibility with a seeker.
async function revealCompatibility(nickName, sessionId) {
  try {
    const r = await axios.post(`${static_store.apiUrl}/reader/compatibility/reveal/`, {
      session_id: sessionId, nick_name: nickName,
    })
    return r.data
  } catch (e) {
    console.error('Reveal compatibility failed:', e)
    return null
  }
}

// A single card's meaning + this reader's symbolism (powers the card-detail modal).
async function getCardDetail(reader, card, reversed) {
  try {
    const params = new URLSearchParams({ reader: reader || '', card: card || '', reversed: reversed ? '1' : '0' })
    const response = await axios.get(`${static_store.apiUrl}/reader/card-detail/?${params.toString()}`)
    return response.data
  } catch (e) {
    console.error('Card detail failed:', e)
    return null
  }
}

// The Lessons tab's Fireside: today's vibe and most-drawn card (API.md §6; no rankings).
async function getCovenantOverview() {
  try {
    const response = await axios.get(`${static_store.apiUrl}/reader/covenant-overview/`)
    return response.data
  } catch (e) {
    console.error('Covenant overview failed:', e)
    return null
  }
}

// ─── Phase 13: Skill Builder pool browsing ───────────────────────── //

function _qs(params) {
  const filtered = Object.entries(params || {}).filter(([, v]) => v !== '' && v != null)
  return filtered.length ? '?' + new URLSearchParams(filtered).toString() : ''
}

async function browseDeckTemplates(filters = {}) {
  try {
    const r = await axios.get(`${static_store.apiUrl}/reader/skills/browse-deck-templates/${_qs(filters)}`)
    return r.data
  } catch (e) {
    console.error('browseDeckTemplates failed:', e)
    return { items: [], total: 0, available: {} }
  }
}

async function browseSymbologyPools(filters = {}) {
  try {
    const r = await axios.get(`${static_store.apiUrl}/reader/skills/browse-symbology-pools/${_qs(filters)}`)
    return r.data
  } catch (e) {
    console.error('browseSymbologyPools failed:', e)
    return { items: [], total: 0, available: {} }
  }
}

async function previewDeckTemplate(slug) {
  try {
    const r = await axios.get(`${static_store.apiUrl}/reader/skills/preview-deck-template/${slug}/`)
    return r.data
  } catch (e) {
    console.error('previewDeckTemplate failed:', e)
    return null
  }
}

async function previewSymbologyPool(slug) {
  try {
    const r = await axios.get(`${static_store.apiUrl}/reader/skills/preview-symbology-pool/${slug}/`)
    return r.data
  } catch (e) {
    console.error('previewSymbologyPool failed:', e)
    return null
  }
}

function _authHeaders() {
  const token = localStorage.getItem('authToken')
  return token ? { Authorization: `Token ${token}` } : {}
}

// Studio per-field polish: fired when the Mystic types a free-form
// reply to the static opener Aida posed for the active field. BE
// shapes the raw reply into a profile-quality value and returns
// {polished, confirmation}. The polished value is written straight
// into the form via the ``studio-assistant:polish-applied`` window
// event in ReaderPage.
async function studioFieldPolish({ field, label, panel, raw_response, current_value, mode = 'magic', protected_tokens = [] }) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/studio/field-polish/`,
    { field, label, panel, raw_response, current_value, mode, protected_tokens },
    { headers: _authHeaders() },
  )
  return r.data
}

// Studio reader-name candidate generator. Takes the Mystic's free-
// form description and returns 4 candidates — one per word-count
// tier — each tagged with cost (1-word = 200 ✦ / 2 = 50 / 3 = 25 /
// 4+ = free) and availability. Doesn't charge anything; the actual
// claim happens via ``studioClaimName``.
async function studioNameOptions(idea, tiers = null) {
  const body = { idea }
  // ``tiers`` (array of word counts, e.g. [1, 2]) constrains both
  // what the LLM is asked to produce AND what the BE returns. Null
  // = all four tiers.
  if (Array.isArray(tiers) && tiers.length) body.tiers = tiers
  const r = await axios.post(
    `${static_store.apiUrl}/reader/studio/name-options/`,
    body,
    { headers: _authHeaders() },
  )
  return r.data
}

// Atomic claim of a reader name. BE checks slug availability,
// validates emerald balance, charges (if non-zero tier), updates
// reader.name + reader.sub_domain. Errors come back as
// {error: 'insufficient_carats' | 'name_taken' | 'invalid_name'}.
async function studioClaimName(name) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/studio/claim-name/`,
    { name },
    { headers: _authHeaders() },
  )
  return r.data
}

// Seeker name picker — same as the Studio Mystic picker (idea -> tier'd
// candidates with emerald cost + availability), but the seeker pool. Address is
// /seeker/<slug>, not a subdomain. Session-auth.
async function seekerNameOptions(session_id, idea, tiers = null) {
  const body = { session_id, idea }
  if (Array.isArray(tiers) && tiers.length) body.tiers = tiers
  const r = await axios.post(`${static_store.apiUrl}/reader/seeker/name-options/`, body)
  return r.data
}

// Atomic claim of a seeker handle. Charges emeralds by word-count tier, sets
// nick_name + name_slug. Errors: {error: 'name_taken' | 'insufficient_carats' | 'invalid_name'}.
async function seekerClaimName(session_id, name) {
  const r = await axios.post(`${static_store.apiUrl}/reader/seeker/claim-name/`, { session_id, name })
  return r.data
}

async function chooseDeckTemplate(templateSlug, opts = {}) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/skills/choose-deck-template/`,
    { template_slug: templateSlug, ...opts },
    { headers: _authHeaders() },
  )
  return r.data
}

async function chooseSymbologyPool(poolSlug, opts = {}) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/skills/choose-symbology-pool/`,
    { pool_slug: poolSlug, ...opts },
    { headers: _authHeaders() },
  )
  return r.data
}

async function listSkillVariants() {
  try {
    const r = await axios.get(
      `${static_store.apiUrl}/reader/skills/variants/`,
      { headers: _authHeaders() },
    )
    return r.data?.variants || []
  } catch (e) {
    console.error('listSkillVariants failed:', e)
    return []
  }
}

async function fetchAvailableVariants() {
  try {
    const r = await axios.get(
      `${static_store.apiUrl}/reader/skills/variants-available/`,
    )
    return r.data?.skills || []
  } catch (e) {
    console.error('fetchAvailableVariants failed:', e)
    return []
  }
}

// Settings-modal flip: change variant_key (e.g. one_card → three_card)
// without churning the chosen deck/card-back/symbology FKs.
// Returns the refreshed enriched variant dict.
async function updateVariantKey(variantId, newVariantKey) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/skills/update-variant-key/`,
    { variant_id: variantId, new_variant_key: newVariantKey },
    { headers: _authHeaders() },
  )
  return r.data?.variant || null
}

async function restoreVariantArchive(archiveId) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/skills/variant-archive/${archiveId}/restore/`,
    {},
    { headers: _authHeaders() },
  )
  return r.data?.variant || null
}

// ── Custom Deck Builder ────────────────────────────────────────────
//
// Phase 5 of CUSTOM_DECK_BUILDER plan. The FE drives a sample-then-
// commit flow: free 5-card preview, charge 50 ✦ per refresh, charge
// 1000 ✦ to commit the remaining 73.
//
// ``customDeckApiUrl`` lets local dev point Custom-Deck calls at a
// non-default backend (e.g., prod) while the rest of the app keeps
// hitting localhost. ``X-Mystic-Source`` tells the BE which env this
// row was created from so MCP can filter dev-test rows for cleanup.
function _customDeckBase() {
  return static_store.customDeckApiUrl || static_store.apiUrl
}
function _customDeckHeaders() {
  return {
    ..._authHeaders(),
    'X-Mystic-Source': static_store.customDeckApiUrl ? 'localhost' : 'prod',
  }
}

async function getCustomDeckDraft() {
  try {
    const r = await axios.get(
      `${_customDeckBase()}/reader/skills/custom-deck/draft/`,
      { headers: _authHeaders() },
    )
    return r.data?.draft || null
  } catch (e) {
    console.error('getCustomDeckDraft failed:', e)
    return null
  }
}

async function startCustomDeck(payload) {
  // payload: {name, theme?, style?, mood?, color_palette?, motif?,
  // era?, energy?, generation_prompt_base?}. Returns
  // {ok, draft, resumed} or 409 {error: 'name_taken', suggestion}.
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/start/`,
    payload,
    { headers: _customDeckHeaders() },
  )
  return r.data
}

async function generateCustomDeckSamples(slug) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/${slug}/generate-samples/`,
    {},
    { headers: _customDeckHeaders() },
  )
  return r.data
}

async function refreshCustomDeckCard(slug, cardId, opts = {}) {
  // opts.promptOverride (optional) — when set, BE updates the deck's
  // generation_prompt_base before re-rendering this one card. The
  // other 77 cards keep their existing baked images.
  const body = {}
  if (opts.promptOverride) body.prompt_override = opts.promptOverride
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/${slug}/refresh-card/${cardId}/`,
    body,
    { headers: _customDeckHeaders() },
  )
  return r.data
}

async function regenerateCustomDeckBack(slug) {
  // Charges 50 ✦ and kicks an async card-back regen. Only valid once
  // ``creation_phase='approved'``. Poll ``getCustomDeckStatus`` for
  // the new ``back_image_url`` to land.
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/${slug}/regenerate-back/`,
    {},
    { headers: _customDeckHeaders() },
  )
  return r.data
}

async function commitCustomDeck(slug) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/${slug}/commit/`,
    {},
    { headers: _customDeckHeaders() },
  )
  return r.data
}

async function getCustomDeckStatus(slug) {
  try {
    const r = await axios.get(
      `${_customDeckBase()}/reader/skills/custom-deck/${slug}/status/`,
      { headers: _authHeaders() },
    )
    return r.data
  } catch (e) {
    console.error('getCustomDeckStatus failed:', e)
    return null
  }
}

// Mystic-favorited platform decks — list / add / remove. Surfaced
// in My Decks → Favorites. Phase 6.
async function listCustomDeckFavorites() {
  try {
    const r = await axios.get(
      `${_customDeckBase()}/reader/skills/custom-deck/favorites/`,
      { headers: _authHeaders() },
    )
    return r.data?.favorites || []
  } catch (e) {
    console.error('listCustomDeckFavorites failed:', e)
    return []
  }
}

async function addCustomDeckFavorite(slug) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/favorites/${slug}/add/`,
    {},
    { headers: _customDeckHeaders() },
  )
  return r.data
}

async function removeCustomDeckFavorite(slug) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/favorites/${slug}/remove/`,
    {},
    { headers: _customDeckHeaders() },
  )
  return r.data
}

// Hard-delete an incomplete (abandoned) custom deck. Frees a slot
// in the 5-incomplete cap. Phase 6.
async function deleteCustomDeck(slug) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/${slug}/delete/`,
    {},
    { headers: _customDeckHeaders() },
  )
  return r.data
}

// Per-card version archive — live + previous renders. Used by the
// CardLightbox's "Archive" section so the operator can see prior
// versions and Make Live one. Phase 6.
async function getCustomDeckCardVersions(slug, cardId) {
  try {
    const r = await axios.get(
      `${_customDeckBase()}/reader/skills/custom-deck/${slug}/card/${cardId}/versions/`,
      { headers: _authHeaders() },
    )
    return r.data || { live: null, archive: [] }
  } catch (e) {
    console.error('getCustomDeckCardVersions failed:', e)
    return { live: null, archive: [] }
  }
}

// Promote an archived card version back to live. Auto-snapshots the
// current live first so nothing is lost. Free. Phase 6.
async function makeCustomDeckCardLive(slug, cardId, versionNumber) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/${slug}/card/${cardId}/make-live/${versionNumber}/`,
    {},
    { headers: _customDeckHeaders() },
  )
  return r.data
}

async function getCustomDeckBackVersions(slug) {
  try {
    const r = await axios.get(
      `${_customDeckBase()}/reader/skills/custom-deck/${slug}/back/versions/`,
      { headers: _authHeaders() },
    )
    return r.data || { live: null, archive: [] }
  } catch (e) {
    console.error('getCustomDeckBackVersions failed:', e)
    return { live: null, archive: [] }
  }
}

async function makeCustomDeckBackLive(slug, versionNumber) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/${slug}/back/make-live/${versionNumber}/`,
    {},
    { headers: _customDeckHeaders() },
  )
  return r.data
}

// List all custom decks owned by the authenticated Mystic across
// every phase. Powers the Custom Deck tab gallery. Phase 6.
async function listMyCustomDecks() {
  try {
    const r = await axios.get(
      `${_customDeckBase()}/reader/skills/custom-deck/my-decks/`,
      { headers: _authHeaders() },
    )
    return r.data?.decks || []
  } catch (e) {
    console.error('listMyCustomDecks failed:', e)
    return []
  }
}

// Load a specific custom deck by slug — used when operator picks one
// from the gallery to view/continue/edit. Returns the same shape as
// getCustomDeckDraft. Phase 6.
async function getCustomDeckBySlug(slug) {
  try {
    const r = await axios.get(
      `${_customDeckBase()}/reader/skills/custom-deck/${slug}/status/`,
      { headers: _authHeaders() },
    )
    return r.data?.draft || null
  } catch (e) {
    console.error('getCustomDeckBySlug failed:', e)
    return null
  }
}

// Mark an in-flight draft as 'abandoned' so the Mystic can start
// fresh without losing the cards. Phase 6.
async function abandonCustomDeck(slug) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/${slug}/abandon/`,
    {},
    { headers: _customDeckHeaders() },
  )
  return r.data
}

// Bulk-refresh all 5 sample cards with an updated generation prompt.
// Charges 100 ✦. Persists the new prompt onto the draft, deletes
// existing sample cards, kicks the wrapper. Phase 6.
async function refreshCustomDeckSamples(slug, generationPromptBase) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/${slug}/refresh-samples/`,
    { generation_prompt_base: generationPromptBase },
    { headers: _customDeckHeaders() },
  )
  return r.data
}

// Aida-driven brand conversation, sync (Phase 6 of plan). Sends a
// chat turn to the BE; gets back ``{message, actions, target_step,
// draft}``. The FE simulates streaming locally on ``message`` for
// the same UX as the wizard chat. Action types:
//   - {type: 'fill_brand_fields', fields: {theme, style, ...}}
//   - {type: 'show_button', key: 'generate_samples' | ... }
async function customDeckChat({ message = '', interaction = 'message' } = {}) {
  const r = await axios.post(
    `${_customDeckBase()}/reader/skills/custom-deck/chat/`,
    { message, interaction },
    { headers: _customDeckHeaders() },
  )
  return r.data
}

// Operator-curated dropdown values for the Custom Deck builder's
// combobox inputs. Returned grouped by field_name. See
// /mcp/custom-deck-presets/ for management. Phase 6.
async function getCustomDeckBrandPresets() {
  try {
    const r = await axios.get(
      `${_customDeckBase()}/reader/skills/custom-deck/brand-presets/`,
      { headers: _authHeaders() },
    )
    return r.data
  } catch (e) {
    console.error('getCustomDeckBrandPresets failed:', e)
    return { presets: {} }
  }
}

export {
  getQuerent, resetPassword, toggleShare, rateReading, captureReferral, acceptTos, sendInvite, getMyFriends,
  sendFriendRequest, respondFriendRequest, setFriendTier, getFriendRequests, shareWithFriends,
  getConversations, getMessageCount, getMessageThread, sendMessage, getDayJournals, setJournalLength, journalSpoken, journalReflect, journalVersions, journalRestore, journalSetVersion, journalConsolidate, getJournalStatus, getJournalDays, getJournalDay, journalAnswer, journalItem, journalPage, journalSeen, journalOnce, journalSetPin, journalUnlock, journalLockRefresh, journalLockOff, journalPasskeyRegisterOptions, journalPasskeyRegister, journalPasskeyOptions, journalPasskeyVerify, journalForgot, journalForgotVerify, journalLine, getJournalThreads, getJournalThread, journalThreadRename, journalThreadMerge, journalThreadTag, journalThreadUntag, getJournalCalendar, getJournalChapter, journalChapterSave, horoscopeOpened, followReader, getMysticsTab, mysticMatch, setFollowOrder, setHomeMystic,
  careIntent, careRename, careDraft, carePreview, getNotifications, setNotifications, setNotification, addPhone, confirmPhone, removePhone, careSend, careContactCheck, careLink, carePrepareLink, careRevoke, careDoor, careSignin, careLanding, careOwnSession, careReceived, careSent, careDeeper, careLeave, careOpener, careThank, careCircle,
  careKeep, careKeepConfirm, getMysticDrops, getStudioFrontDoor, mysticPageArrive, getFirstReading, lessonGuestPlace, readingReflection, talkMore, carePeople, carePassword, introductionMoment, roomRecommend,
  getPersonalHoroscope, customizePersonalHoroscope,
  getPersonalAffirmation, customizePersonalAffirmation, setPersonalAffirmation,
  getAltarSpace, saveAltarSpace, generateAltarImage, selectAltarImage, assistAltarField, compileAltarPrompt, markAltarRiteDone, markHoroscopeOpened,
  getMirrorProfile, generateMirrorAbout, shareMirrorAbout, getImageGallery, toggleFavoriteImage, keepImage, getJournalKept, journalUnkeep,
  generateEvaluation, slantEvaluation, setEvaluationLength, restoreEvaluation, shareEvaluation,
  setAltarPublic, setAffirmationPublic, saveQuerentAccount,
  changePassword, deleteQuerentAccount,
  getPlatformQuests, claimQuest, claimAllQuests, getNav, navSeen, questAct, guestPlace, markMysticUnlockSeen,
  getMysticSeat, subscribeMysticSeat, deleteReadingSession,
  getMyReader, getMyChart, getStudioReaders, updateReader, setReaderAvatarFromUrl,
  browseStudioPortraits, generateStudioPortrait, getAltarGreeting, altarSetSign, getDoorwayGreeting,
  getCommunityPulse, getLatestDeck, getDeckRating, rateDeck, getMysticsVisited, getDayDrops, getDayHoroscopes, getCommunityPolls, castCommunityVote, generateCandidate, submitCandidate, getMyCandidates, getMySubmission, deleteCandidate, getReaderDetail, getReadingPreview, getLessonRelated, getSeekerFeed,
  conciergeChat, conciergeConfirm, creditAltarDwell,
  toggleReadingSupport, offerReadingGrace,
  getCovenantOverview, searchLessons, searchAll, logSearch, getSearchSuggest, getFoundInSearch, setReadForTopics, getCardDetail, getSeekerDetail, revealCompatibility, browseDeckTemplates, browseSymbologyPools,
  previewDeckTemplate, previewSymbologyPool, chooseDeckTemplate, chooseSymbologyPool,
  listSkillVariants,
  fetchAvailableVariants, updateVariantKey, restoreVariantArchive,
  getCustomDeckDraft, startCustomDeck, generateCustomDeckSamples, getCustomDeckBrandPresets,
  customDeckChat, abandonCustomDeck, refreshCustomDeckSamples,
  listMyCustomDecks, getCustomDeckBySlug,
  getCustomDeckCardVersions, makeCustomDeckCardLive,
  getCustomDeckBackVersions, makeCustomDeckBackLive,
  listCustomDeckFavorites, addCustomDeckFavorite, removeCustomDeckFavorite,
  deleteCustomDeck,
  refreshCustomDeckCard, regenerateCustomDeckBack,
  commitCustomDeck, getCustomDeckStatus, studioFieldPolish,
  studioNameOptions, studioClaimName,
  seekerNameOptions, seekerClaimName,
  // Reading Session Studio panel — Phase 9
  listReadingSessionSkills, getReadingSessionTree, updateReadingSessionVariant,
  updateReadingMoment, updateReadingMomentLimit, previewReadingBeat,
  updateInterviewPace, runInterviewPlaythrough, updateMeaningSource, updatePositionLabel, updatePositionStream,
  getPositionSuggestions, getTitleSuggestions, updateMysticism,
  publishReadingGame, getPublishDiff, listSnapshots, restoreGameSnapshot,
  listBeatHistory, restoreBeatVersion,
  getReaderCardMeaning, getCardPool, runCardRevealPreview, runReadingPreview, runImagePreview,
  // Seed-version diff/merge — Phase 11
  getSeedVersionDiff, adoptSeedVersion,
};

// ─── Reading Session Studio (Phase 9) ────────────────────────────
// Powers the per-skill panel that authoring + pricing edits flow
// through.

async function listReadingSessionSkills() {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/reading-session/skills/`,
    { headers: _authHeaders() },
  )
  return r.data
}

async function getReadingSessionTree(skillKey) {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/`,
    { headers: _authHeaders() },
  )
  return r.data
}

async function updateReadingSessionVariant(variantId, payload) {
  const r = await axios.patch(
    `${static_store.apiUrl}/reader/skill-variant/${variantId}/`,
    payload,
    { headers: _authHeaders() },
  )
  return r.data
}

// Plain-language journey beat ($moment_intent) for one slug — grounding,
// interview, card_reveal_<role>, reading, image.
async function updateReadingMoment(skillKey, slug, intent) {
  const r = await axios.patch(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/moment/${slug}/`,
    { intent },
    { headers: _authHeaders() },
  )
  return r.data
}

// Set the reader-wide Mysticism dial (grounded / mystical / cosmic) — the
// voice-intensity that colors reveals, the reading, images, and drops.
async function updateMysticism(level) {
  const r = await axios.patch(
    `${static_store.apiUrl}/reader/profile/mysticism/`,
    { mysticism: level },
    { headers: _authHeaders() },
  )
  return r.data
}

// Set the card-reveal meaning source — 'symbolic' (the reader's authored
// meanings) or 'generic' (canonical RWS). Draft; goes live on Publish.
async function updateMeaningSource(skillKey, source) {
  const r = await axios.patch(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/meaning-source/`,
    { meaning_source: source },
    { headers: _authHeaders() },
  )
  return r.data
}

// Rename a card position (e.g. the 1-card "Focus" position). Blank label
// clears the override. Draft; goes live on Publish and is injected into the
// reveal pre-line ("For the <label> card, you selected …").
async function updatePositionLabel(skillKey, role, label) {
  const r = await axios.patch(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/position-label/`,
    { role, label },
    { headers: _authHeaders() },
  )
  return r.data
}

// Toggle whether a card position echoes into the chat window when drawn /
// replayed (Studio → Card Selection). Returns the updated reading_style.
async function updatePositionStream(skillKey, role, mode) {
  const r = await axios.patch(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/position-stream/`,
    { role, mode },
    { headers: _authHeaders() },
  )
  return r.data
}

// Three short, reader-relevant alternative names for a card position (e.g. the
// 1-card "Focus") — the digital assistant's pick-one offer. Returns [name, …].
async function getPositionSuggestions(skillKey, role, n = 3) {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/position-suggestions/`,
    { params: { role, n }, headers: _authHeaders() },
  )
  return (r.data && r.data.suggestions) || []
}

// Reader-relevant DIRECTIONS for the reading's summary title — the Title-
// direction field's pick-one offer. Returns [direction, …].
async function getTitleSuggestions(skillKey, n = 4) {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/title-suggestions/`,
    { params: { n }, headers: _authHeaders() },
  )
  return (r.data && r.data.suggestions) || []
}

// Set the reader's Interview tempo (quick / balanced / patient) — the single
// pacing dial that shapes how the interview opens and when the cards are offered.
async function updateInterviewPace(skillKey, pace) {
  const r = await axios.patch(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/interview-pace/`,
    { pace },
    { headers: _authHeaders() },
  )
  return r.data
}

// A hand of real cards for the card-selection simulator's face-down picker.
// Returns [{card_id, card_name, image_url}].
async function getCardPool(skillKey, n = 8) {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/card-pool/?n=${n}`,
    { headers: _authHeaders() },
  )
  // { cards: [...], card_back_image_url }
  return r.data || { cards: [] }
}

// Mirror one card reveal for the simulator — { card_name, card_image_url,
// pre_line, output, reversed }. Honors the chosen meaning source + word budget.
async function runCardRevealPreview(skillKey, payload = {}) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/card-reveal-preview/`,
    payload,
    { headers: _authHeaders() },
  )
  return r.data
}

// Mirror the live multi-card reading for the simulator — a real hand from the
// reader's deck at the spread's size, woven into one synthesis under the word
// cap. { cards: [...], output, beat }.
async function runReadingPreview(skillKey, payload = {}) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/reading-preview/`,
    payload,
    { headers: _authHeaders() },
  )
  return r.data
}

// Generate a REAL illustration preview (cheapest settings) for the Image
// Direction beat. A deliberate spend — only called from the Generate button.
// { image_url, description }.
async function runImagePreview(skillKey, payload = {}) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/image-preview/`,
    payload,
    { headers: _authHeaders() },
  )
  return r.data
}

// A card's meaning under both sources (generic RWS + the reader's symbolic
// layer) — powers the deck editor's Card Meaning section and previews what the
// Symbolic reveal source delivers. { card_name, generic{}, symbolic{}, has_symbolic }.
async function getReaderCardMeaning(cardId) {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/deck/card/${cardId}/meaning/`,
    { headers: _authHeaders() },
  )
  return r.data
}

// ── Draft → Publish gate ────────────────────────────────────────────────
// Publish Live: promote the Studio draft (beats + price) to live and stamp a
// Game History snapshot. Returns { ok, published_at, reading_style }.
async function publishReadingGame(skillKey) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/publish/`,
    {},
    { headers: _authHeaders() },
  )
  track('publish_game', { skill: skillKey })
  return r.data
}

// The Publish modal's "what changed" diff — draft vs live, before→after.
// Returns [{ label, before, after }].
async function getPublishDiff(skillKey) {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/publish-diff/`,
    { headers: _authHeaders() },
  )
  return (r.data && r.data.changes) || []
}

// Unified Snapshots timeline — published versions + spread-switch backups in
// one list. Returns [{ kind, id, at, label, spread, price }].
async function listSnapshots(skillKey) {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/snapshots/`,
    { headers: _authHeaders() },
  )
  return r.data?.snapshots || []
}

// Restore a published game snapshot into the DRAFT (review, then re-publish).
async function restoreGameSnapshot(skillKey, snapshotId) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/game-history/${snapshotId}/restore/`,
    {},
    { headers: _authHeaders() },
  )
  return r.data
}

// Per-beat history — list a beat's prior approved prompts (newest first).
async function listBeatHistory(skillKey, slug) {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/moment/${slug}/history/`,
    { headers: _authHeaders() },
  )
  return r.data?.versions || []
}

// Restore one beat prompt version into the DRAFT for that beat.
async function restoreBeatVersion(skillKey, slug, versionId) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/moment/${slug}/history/${versionId}/restore/`,
    {},
    { headers: _authHeaders() },
  )
  return r.data
}

// Play a short interview as a transcript so the Mystic can SEE the pace work —
// the reader opens, the seeker (canned) replies, and the reader offers the cards
// at a point set by pace. Returns { pace, offer_after, transcript:[{role,text,kind}] }.
async function runInterviewPlaythrough(skillKey, payload = {}) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/interview-playthrough/`,
    payload,
    { headers: _authHeaders() },
  )
  return r.data
}

// Set a beat's response length cap (characters) — the simulator keeps that
// beat's reading within this budget.
async function updateReadingMomentLimit(skillKey, slug, limit) {
  const r = await axios.patch(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/moment/${slug}/limit/`,
    { limit },
    { headers: _authHeaders() },
  )
  return r.data
}

// Per-beat preview for the Studio "Reading Journey" simulator. Renders ONE
// journey beat via a lightweight, side-effect-free LLM call. ``payload`` may
// carry { moment_intent, reader_slant, sample_input } so the Mystic can preview
// unsaved edits live. Returns { output, compiled_prompt, beat }.
async function previewReadingBeat(skillKey, slug, payload = {}) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/moment/${slug}/preview/`,
    payload,
    { headers: _authHeaders() },
  )
  return r.data
}

// ─── Seed-version diff/merge (Phase 11) ─────────────────────────
// Drives the "new version available" banner inside the Reading
// Session panel. The diff response groups by instruction set and
// flags each step as unchanged/changed/added/removed. The adopt
// call submits per-step adopt/keep decisions and re-stamps the
// rows at the platform's current SEED_VERSION.

async function getSeedVersionDiff(skillKey) {
  const r = await axios.get(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/seed-diff/`,
    { headers: _authHeaders() },
  )
  return r.data
}

async function adoptSeedVersion(skillKey, decisions) {
  const r = await axios.post(
    `${static_store.apiUrl}/reader/reading-session/${skillKey}/seed-adopt/`,
    { decisions },
    { headers: _authHeaders() },
  )
  return r.data
}

// ── Concierge (Mystic AIda feedback loop) ────────────────────────────────────
async function conciergeChat(message, threadId) {
  const r = await axios.post(
    `${static_store.apiUrl}/concierge/chat/`,
    { message, thread_id: threadId || null, session_id: store.session_id },
    { headers: _authHeaders() },
  )
  return r.data
}

async function conciergeConfirm(payload) {
  const r = await axios.post(
    `${static_store.apiUrl}/concierge/confirm/`,
    { ...payload, session_id: store.session_id },
    { headers: _authHeaders() },
  )
  return r.data
}

// Altar dwell — the seeker spent a minute at their Altar → credit the daily reward.
async function creditAltarDwell() {
  const r = await axios.post(`${static_store.apiUrl}/reader/altar-dwell/`,
    { session_id: store.session_id }, { headers: _authHeaders() })
  return r.data
}
